import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { THEME_CHOICES, useTheme, type ThemeChoice } from '../../lib/theme.js';
import { Button } from '../ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';

const ICONS: Record<ThemeChoice, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

export interface ThemeToggleProps {
  /**
   * `full` names the current theme, `compact` is the icon alone with a
   * menu, and `switch` drops the menu and flips light↔dark on click.
   */
  variant?: 'full' | 'compact' | 'switch';
  className?: string;
}

/**
 * Three states, not two. "System" has to stay reachable — someone who
 * switches their laptop to dark at sunset expects the app to come with it,
 * and a two-way toggle silently opts them out of that forever.
 */
export function ThemeToggle({ variant = 'compact', className }: ThemeToggleProps) {
  const { t } = useTranslation('common');
  const { theme, resolvedTheme, setTheme, toggleTheme } = useTheme();

  if (variant === 'switch') {
    // Announces where the click *goes*, not where it is — a button labelled
    // with its current state reads as a broken toggle to a screen reader.
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    const NextIcon = ICONS[next];

    return (
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={toggleTheme}
        aria-label={t(`theme.switchTo.${next}`)}
        title={t(`theme.switchTo.${next}`)}
        className={className}
      >
        <NextIcon />
      </Button>
    );
  }

  const Icon = ICONS[theme];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === 'full' ? 'sm' : 'icon-sm'}
          className={cn(variant === 'full' && 'gap-2', className)}
          aria-label={t('theme.label')}
        >
          <Icon />
          {variant === 'full' ? t(`theme.${theme}`) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('theme.label')}</DropdownMenuLabel>
        {THEME_CHOICES.map((choice) => {
          const ChoiceIcon = ICONS[choice];
          return (
            <DropdownMenuItem
              key={choice}
              onSelect={() => setTheme(choice)}
              className={choice === theme ? 'font-semibold' : undefined}
            >
              <ChoiceIcon />
              <span className="flex-1">{t(`theme.${choice}`)}</span>
              {choice === theme ? <Check /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
